import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiClientError, SaveConflictError, canonicalJson, createMutationId, createSavePayload, getDeviceId, type ApiClient, type SaveDocument } from '../api'
import { loadStoredSaveRecord } from '../storage'
import { type GameState } from '../game'
import type { CloudStatus, SaveSelection } from '../view-types'
import type { LocalStateGuard } from './useLocalGame'
import { useVipEntitlement } from './useVipEntitlement'

const CONTENT_VERSION = import.meta.env.VITE_CONTENT_VERSION || 'content-dev-v1'

type PendingCloudSave = { state: GameState; version: number }
type SaveSyncQueue = { generation: number; userId: string; inFlight: Promise<void> | null; pending: PendingCloudSave | null }

type UseCloudSaveOptions = {
  api: ApiClient
  activeUserId: string | null
  authResolved: boolean
  ownerGeneration: number
  localLoaded: boolean
  saveOwner: string | null
  localSave: GameState | null
  localSaveUpdatedAt: string | null
  guestSave: GameState | null
  guestSaveUpdatedAt: string | null
  isCurrentOwner: (generation: number, userId: string | null) => boolean
  currentLocalGuard: () => LocalStateGuard
  isCurrentLocalGuard: (generation: number, userId: string | null, guard: LocalStateGuard) => boolean
  getLatestLocal: () => GameState | null
  applyCloudDocument: (document: SaveDocument, generation: number, owner: string, expectedGuard?: LocalStateGuard) => boolean
  projectRevision: (candidate: GameState, localVersion: number, revision: number, updatedAt: string, owner: string, generation: number) => Promise<boolean>
  setProjectionReady: (ready: boolean) => void
  discardGuestSave: () => Promise<void>
  refreshAuth: () => Promise<unknown>
}

function hasProgress(state: GameState | null): state is GameState {
  return Boolean(state?.runStarted || state?.history.length || state?.completedRuns || state?.endingsCollected.length || state?.fateCollected.length)
}

function documentKey(document: SaveDocument | null): string {
  return document ? `${document.revision}:${canonicalJson(document.state)}` : ''
}

function isNewer(left: string | null, right: string | null): boolean {
  return Date.parse(left || '') > Date.parse(right || '')
}

export function useCloudSave({ api, activeUserId, authResolved, ownerGeneration, localLoaded, saveOwner, localSave, localSaveUpdatedAt, guestSave, guestSaveUpdatedAt, isCurrentOwner, currentLocalGuard, isCurrentLocalGuard, getLatestLocal, applyCloudDocument, projectRevision, setProjectionReady, discardGuestSave, refreshAuth }: UseCloudSaveOptions) {
  const [cloudDocument, setCloudDocument] = useState<SaveDocument | null>(null)
  const [cloudStatus, setCloudStatus] = useState<CloudStatus>('idle')
  const [cloudError, setCloudError] = useState('')
  const [saveSelection, setSaveSelection] = useState<SaveSelection | null>(null)
  const [cloudActionBusy, setCloudActionBusy] = useState(false)
  const [cloudActionError, setCloudActionError] = useState('')
  const cloudDocumentRef = useRef<SaveDocument | null>(null)
  const saveSelectionRef = useRef<SaveSelection | null>(null)
  const syncReadyRef = useRef(false)
  const lastSyncedKeyRef = useRef('')
  const syncTimerRef = useRef<number | null>(null)
  const syncQueueRef = useRef<SaveSyncQueue | null>(null)
  const loadedGenerationRef = useRef<number | null>(null)
  const localSaveUpdatedAtRef = useRef<string | null>(localSaveUpdatedAt)

  useEffect(() => { localSaveUpdatedAtRef.current = localSaveUpdatedAt }, [localSaveUpdatedAt])

  const setSelection = useCallback((selection: SaveSelection | null) => {
    saveSelectionRef.current = selection
    setSaveSelection(selection)
  }, [])

  const markDocument = useCallback((document: SaveDocument | null) => {
    cloudDocumentRef.current = document
    setCloudDocument(document)
  }, [])

  const flushCloudSave = useCallback(async (candidate: GameState, generation = ownerGeneration, userId = activeUserId, localVersion?: number): Promise<void> => {
    const version = localVersion ?? currentLocalGuard().version
    if (!userId || !syncReadyRef.current || saveSelectionRef.current || !isCurrentOwner(generation, userId)) return
    const candidateGuard: LocalStateGuard = { version, key: canonicalJson(candidate.coreState) }
    if (!isCurrentLocalGuard(generation, userId, candidateGuard)) {
      const latest = getLatestLocal()
      if (latest && isCurrentOwner(generation, userId)) return flushCloudSave(latest, generation, userId, currentLocalGuard().version)
      return
    }
    const queue = syncQueueRef.current
    if (!queue || queue.generation !== generation || queue.userId !== userId) return
    if (queue.inFlight) {
      queue.pending = { state: candidate, version }
      return queue.inFlight
    }

    setCloudStatus('pending')
    setCloudError('')
    let request!: Promise<void>
    request = (async () => {
      try {
        const document = await api.putSave(createSavePayload(candidate, CONTENT_VERSION, {
          expectedRevision: cloudDocumentRef.current?.revision || 0
        }))
        if (!isCurrentOwner(generation, userId)) return
        await projectRevision(candidate, version, document.revision, document.updatedAt, userId, generation)
        if (!isCurrentOwner(generation, userId)) return
        markDocument(document)
        if (isCurrentLocalGuard(generation, userId, candidateGuard)) {
          lastSyncedKeyRef.current = canonicalJson(candidate.coreState)
          setCloudStatus('synced')
        } else {
          const latest = getLatestLocal()
          if (latest) queue.pending = { state: latest, version: currentLocalGuard().version }
          setCloudStatus('pending')
        }
      } catch (error) {
        if (!isCurrentOwner(generation, userId)) return
        if (error instanceof SaveConflictError) {
          try {
            const remote = await api.getSave()
            if (!isCurrentOwner(generation, userId)) return
            markDocument(remote)
            const latest = getLatestLocal() || candidate
            if (remote && !isNewer(localSaveUpdatedAtRef.current, remote.updatedAt)) {
              applyCloudDocument(remote, generation, userId)
              lastSyncedKeyRef.current = canonicalJson(remote.state)
              setCloudStatus('synced')
              setCloudError('另一台设备保存得更晚，已切换到最新云存档。')
            } else {
              queue.pending = { state: latest, version: currentLocalGuard().version }
              setCloudStatus('pending')
            }
          } catch (reloadError) {
            syncReadyRef.current = false
            setCloudStatus('offline')
            setCloudError(reloadError instanceof Error ? reloadError.message : '暂时无法确认最新云存档，离线进度仍已保留。')
          }
        } else if (error instanceof ApiClientError && error.status === 401) {
          syncReadyRef.current = false
          setCloudStatus('offline')
          setCloudError('登录会话已失效，请重新登录。')
          void refreshAuth()
        } else {
          syncReadyRef.current = false
          setCloudStatus('offline')
          setCloudError('当前处于离线状态，账号进度已保存在本设备，联网后会自动同步。')
        }
      } finally {
        if (queue.inFlight === request) queue.inFlight = null
        const pending = queue.pending
        queue.pending = null
        if (pending && isCurrentOwner(generation, userId) && !saveSelectionRef.current) {
          await flushCloudSave(pending.state, generation, userId, pending.version)
        }
      }
    })()
    queue.inFlight = request
    await request
  }, [activeUserId, api, applyCloudDocument, currentLocalGuard, getLatestLocal, isCurrentLocalGuard, isCurrentOwner, markDocument, ownerGeneration, projectRevision, refreshAuth])

  const finishGuestComparison = useCallback(async (document: SaveDocument | null, generation: number, owner: string) => {
    const guestRecord = await loadStoredSaveRecord(null)
    if (!isCurrentOwner(generation, owner)) return
    const candidate = guestRecord?.state || guestSave
    const candidateTime = guestRecord?.savedAt || guestSaveUpdatedAt
    if (!hasProgress(candidate)) {
      setSelection(null)
      setCloudStatus('synced')
      setProjectionReady(true)
      return
    }
    if (document && canonicalJson(candidate.coreState) === canonicalJson(document.state)) {
      await discardGuestSave()
      if (!isCurrentOwner(generation, owner)) return
      setSelection(null)
      setCloudStatus('synced')
      setProjectionReady(true)
      return
    }
    if (!document) {
      try {
        const imported = await api.importSave({
          ...createSavePayload(candidate, CONTENT_VERSION, { expectedRevision: 0 }),
          legacySource: 'sun_v5_v1'
        })
        if (!isCurrentOwner(generation, owner)) return
        markDocument(imported)
        applyCloudDocument(imported, generation, owner)
        lastSyncedKeyRef.current = canonicalJson(imported.state)
        await discardGuestSave()
        if (!isCurrentOwner(generation, owner)) return
        syncReadyRef.current = true
        setSelection(null)
        setCloudStatus('synced')
        setProjectionReady(true)
      } catch {
        syncReadyRef.current = false
        setCloudStatus('offline')
        setCloudError('游客进度已保留；连接云端后会继续完成登录存档。')
        setProjectionReady(true)
      }
      return
    }
    setSelection({
      guest: { state: candidate, updatedAt: candidateTime || new Date(0).toISOString() },
      cloud: document
    })
    setCloudStatus('choosing')
    setCloudError('检测到游客存档，请选择唯一保留的一份。')
    setProjectionReady(true)
  }, [api, applyCloudDocument, discardGuestSave, guestSave, guestSaveUpdatedAt, isCurrentOwner, markDocument, setProjectionReady, setSelection])

  const reconcileCloud = useCallback(async (generation = ownerGeneration, owner = activeUserId) => {
    if (!owner || !authResolved || !localLoaded || saveOwner !== owner || !isCurrentOwner(generation, owner)) return
    setCloudStatus('loading')
    setCloudError('')
    setCloudActionError('')
    try {
      let remote = await api.getSave()
      if (!isCurrentOwner(generation, owner)) return
      markDocument(remote)
      const local = getLatestLocal()

      if (remote && local && canonicalJson(local.coreState) !== canonicalJson(remote.state)) {
        if (isNewer(localSaveUpdatedAtRef.current, remote.updatedAt)) {
          syncReadyRef.current = true
          await flushCloudSave(local, generation, owner, currentLocalGuard().version)
          if (!syncReadyRef.current) {
            setProjectionReady(true)
            return
          }
          remote = cloudDocumentRef.current
        } else {
          applyCloudDocument(remote, generation, owner)
        }
      } else if (remote && !local) {
        applyCloudDocument(remote, generation, owner)
      } else if (!remote && local && hasProgress(local)) {
        syncReadyRef.current = true
        await flushCloudSave(local, generation, owner, currentLocalGuard().version)
        if (!syncReadyRef.current) {
          setProjectionReady(true)
          return
        }
        remote = cloudDocumentRef.current
      }

      if (!isCurrentOwner(generation, owner)) return
      syncReadyRef.current = true
      if (remote) lastSyncedKeyRef.current = canonicalJson(remote.state)
      else if (local) lastSyncedKeyRef.current = canonicalJson(local.coreState)
      await finishGuestComparison(remote, generation, owner)
    } catch (error) {
      if (!isCurrentOwner(generation, owner)) return
      syncReadyRef.current = false
      setCloudStatus('offline')
      setCloudError(error instanceof ApiClientError && error.status === 401
        ? '登录会话已失效，请重新登录。'
        : '当前处于离线状态，账号进度已保存在本设备，联网后会自动同步。')
      setProjectionReady(true)
      if (error instanceof ApiClientError && error.status === 401) void refreshAuth()
    }
  }, [activeUserId, api, applyCloudDocument, authResolved, currentLocalGuard, finishGuestComparison, flushCloudSave, getLatestLocal, isCurrentOwner, localLoaded, markDocument, ownerGeneration, refreshAuth, saveOwner, setProjectionReady])

  useEffect(() => {
    if (!authResolved) return
    const generation = ownerGeneration
    const owner = activeUserId
    loadedGenerationRef.current = null
    syncReadyRef.current = false
    syncQueueRef.current = owner ? { generation, userId: owner, inFlight: null, pending: null } : null
    lastSyncedKeyRef.current = ''
    markDocument(null)
    setSelection(null)
    setCloudError('')
    setCloudActionError('')
    setCloudActionBusy(false)
    if (!owner) {
      setCloudStatus('idle')
      loadedGenerationRef.current = generation
      setProjectionReady(true)
    } else {
      setCloudStatus('loading')
      setProjectionReady(false)
    }
    return () => {
      if (syncTimerRef.current !== null) window.clearTimeout(syncTimerRef.current)
      syncTimerRef.current = null
    }
  }, [activeUserId, authResolved, markDocument, ownerGeneration, setProjectionReady, setSelection])

  useEffect(() => {
    if (!authResolved || !activeUserId || saveOwner !== activeUserId || !localLoaded || loadedGenerationRef.current === ownerGeneration) return
    loadedGenerationRef.current = ownerGeneration
    void reconcileCloud(ownerGeneration, activeUserId)
  }, [activeUserId, authResolved, localLoaded, ownerGeneration, reconcileCloud, saveOwner])

  useEffect(() => {
    const owner = activeUserId
    if (!authResolved || saveOwner !== owner || !localLoaded || !localSave || !owner || !syncReadyRef.current || saveSelection) return
    if (canonicalJson(localSave.coreState) === lastSyncedKeyRef.current) return
    if (syncTimerRef.current !== null) window.clearTimeout(syncTimerRef.current)
    const generation = ownerGeneration
    syncTimerRef.current = window.setTimeout(() => {
      syncTimerRef.current = null
      const latest = getLatestLocal()
      if (latest) void flushCloudSave(latest, generation, owner)
    }, 350)
    return () => {
      if (syncTimerRef.current !== null) window.clearTimeout(syncTimerRef.current)
      syncTimerRef.current = null
    }
  }, [activeUserId, authResolved, flushCloudSave, getLatestLocal, localLoaded, localSave, ownerGeneration, saveOwner, saveSelection])

  useEffect(() => {
    if (!activeUserId) return
    const onOnline = () => void reconcileCloud(ownerGeneration, activeUserId)
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [activeUserId, ownerGeneration, reconcileCloud])

  const chooseGuestSave = useCallback(async () => {
    const selection = saveSelectionRef.current
    const owner = activeUserId
    const generation = ownerGeneration
    if (!selection || !owner || !isCurrentOwner(generation, owner)) return
    setCloudActionBusy(true)
    setCloudActionError('')
    try {
      const currentRemote = await api.getSave()
      if (!isCurrentOwner(generation, owner)) return
      if (!currentRemote || documentKey(currentRemote) !== documentKey(selection.cloud)) {
        if (currentRemote) {
          markDocument(currentRemote)
          setSelection({ ...selection, cloud: currentRemote })
          setCloudActionError('云端存档刚刚更新，请按新的时间标记重新选择。')
        } else {
          setCloudActionError('云端存档刚刚被清除，请重试。')
        }
        return
      }
      const guestRecord = await loadStoredSaveRecord(null)
      const candidate = guestRecord?.state || selection.guest.state
      const document = await api.importSave({
        ...createSavePayload(candidate, CONTENT_VERSION, { expectedRevision: currentRemote.revision }),
        legacySource: 'sun_v5_v1'
      })
      if (!isCurrentOwner(generation, owner)) return
      markDocument(document)
      applyCloudDocument(document, generation, owner)
      lastSyncedKeyRef.current = canonicalJson(document.state)
      await discardGuestSave()
      if (!isCurrentOwner(generation, owner)) return
      setSelection(null)
      syncReadyRef.current = true
      setCloudStatus('synced')
      setCloudError('已保留游客存档，原云端存档已被替换。')
    } catch (error) {
      if (isCurrentOwner(generation, owner)) setCloudActionError(error instanceof Error ? error.message : '保存游客进度失败，请重试。')
    } finally {
      if (isCurrentOwner(generation, owner)) setCloudActionBusy(false)
    }
  }, [activeUserId, api, applyCloudDocument, discardGuestSave, isCurrentOwner, markDocument, ownerGeneration, setSelection])

  const chooseCloudSave = useCallback(async () => {
    const selection = saveSelectionRef.current
    const owner = activeUserId
    const generation = ownerGeneration
    if (!selection || !owner || !isCurrentOwner(generation, owner)) return
    setCloudActionBusy(true)
    setCloudActionError('')
    try {
      const remote = await api.getSave()
      if (!isCurrentOwner(generation, owner)) return
      if (!remote) {
        setCloudActionError('云端存档刚刚被清除，请重新选择。')
        return
      }
      if (documentKey(remote) !== documentKey(selection.cloud)) {
        markDocument(remote)
        setSelection({ ...selection, cloud: remote })
        setCloudActionError('云端存档刚刚更新，请按新的时间标记重新选择。')
        return
      }
      applyCloudDocument(remote, generation, owner)
      lastSyncedKeyRef.current = canonicalJson(remote.state)
      await discardGuestSave()
      if (!isCurrentOwner(generation, owner)) return
      setSelection(null)
      syncReadyRef.current = true
      setCloudStatus('synced')
      setCloudError('已保留云端存档，游客存档已删除。')
    } catch (error) {
      if (isCurrentOwner(generation, owner)) setCloudActionError(error instanceof Error ? error.message : '确认云端存档失败，请重试。')
    } finally {
      if (isCurrentOwner(generation, owner)) setCloudActionBusy(false)
    }
  }, [activeUserId, api, applyCloudDocument, discardGuestSave, isCurrentOwner, markDocument, ownerGeneration, setSelection])

  const resetCloud = useCallback(async (scope: 'run' | 'all') => {
    const owner = activeUserId
    const generation = ownerGeneration
    if (!owner || !isCurrentOwner(generation, owner)) return
    const label = scope === 'all' ? '清除全部收藏与进度' : '重开当前人生'
    if (!window.confirm(`确定${label}吗？云端只保留重置后的最新存档，无法恢复旧版本。`)) return
    setCloudActionBusy(true)
    setCloudActionError('')
    try {
      const document = await api.resetSave({ mutationId: createMutationId(), expectedRevision: cloudDocumentRef.current?.revision || 0, deviceId: getDeviceId(), scope })
      if (!isCurrentOwner(generation, owner)) return
      markDocument(document)
      applyCloudDocument(document, generation, owner)
      lastSyncedKeyRef.current = canonicalJson(document.state)
      setCloudStatus('synced')
    } catch (error) {
      if (isCurrentOwner(generation, owner)) setCloudActionError(error instanceof Error ? error.message : '云端重置失败，请重试。')
    } finally {
      if (isCurrentOwner(generation, owner)) setCloudActionBusy(false)
    }
  }, [activeUserId, api, applyCloudDocument, isCurrentOwner, markDocument, ownerGeneration])

  const syncCloudNow = useCallback(() => {
    const owner = activeUserId
    if (!owner || !isCurrentOwner(ownerGeneration, owner)) return
    if (saveSelectionRef.current) {
      setCloudError('请先选择唯一保留的存档。')
      return
    }
    if (!syncReadyRef.current) {
      void reconcileCloud(ownerGeneration, owner)
      return
    }
    const latest = getLatestLocal()
    if (!latest) {
      setCloudError('还没有可同步的人生进度。')
      return
    }
    void flushCloudSave(latest, ownerGeneration, owner)
  }, [activeUserId, flushCloudSave, getLatestLocal, isCurrentOwner, ownerGeneration, reconcileCloud])

  const { vipStatus, svipStatus, vipError, beginVipPayment, getVipPayment, confirmVipPayment, refreshEntitlements } = useVipEntitlement({ api, activeUserId, ownerGeneration, isCurrentOwner })

  return {
    cloudDocument,
    cloudStatus,
    cloudError,
    saveSelection,
    vipStatus,
    svipStatus,
    vipError,
    cloudActionBusy,
    cloudActionError,
    chooseGuestSave,
    chooseCloudSave,
    resetCloud,
    syncCloudNow,
    beginVipPayment,
    getVipPayment,
    confirmVipPayment,
    refreshEntitlements,
    flushCloudSave
  }
}
